import { useMemo, useState } from "react"

/**
 * Officer-readable rendering of a proposed rewrite. The backend hands us a
 * unified diff (persist_commit's diff_preview); officers should never have to
 * read `@@` hunks or +/- markers, so we fold each contiguous changed run into
 * a "Current text → Revised text" card and strip markdown syntax from the
 * displayed text.
 */

export interface ChangeBlock {
  removed: string[]
  added: string[]
}

/** Contiguous +/- runs become one block; context/hunk/header lines end a block. */
export function parseChangeBlocks(raw: string): ChangeBlock[] {
  const blocks: ChangeBlock[] = []
  let current: ChangeBlock | null = null
  for (const line of raw.split("\n")) {
    if (line.startsWith("+++ ") || line.startsWith("--- ") || line.startsWith("@@")) {
      current = null
      continue
    }
    if (line.startsWith("+") || line.startsWith("-")) {
      if (!current) {
        current = { removed: [], added: [] }
        blocks.push(current)
      }
      if (line.startsWith("+")) current.added.push(line.slice(1))
      else current.removed.push(line.slice(1))
    } else {
      current = null
    }
  }
  // Drop whitespace-only blocks — they read as "nothing changed" to an officer.
  return blocks.filter((b) => b.removed.join("").trim() || b.added.join("").trim())
}

/**
 * Order paragraphs are numbered `**4.2.**` and sections `## 4. TITLE`; a diff
 * line that touches one carries the marker at line start. These are ORDER
 * paragraph numbers (¶), not statutory sections (§) — the UI must never
 * conflate the two in a tax order.
 */
const SECTION_RES = [
  /\*\*(\d+(?:\.\d+)*)\.?\*\*/, // **4.2.** paragraph numbering
  /^#{1,6}\s*(\d+(?:\.\d+)*)\.?\s/, // ## 4. SECTION HEADING
]

/** Order-paragraph numbers a single change block touches, in document order. */
export function paragraphsInBlock(block: ChangeBlock): string[] {
  const found: string[] = []
  for (const line of [...block.removed, ...block.added]) {
    for (const re of SECTION_RES) {
      const m = re.exec(line)
      if (m && !found.includes(m[1])) found.push(m[1])
    }
  }
  return found
}

/** Unique order-paragraph numbers across the whole diff, in document order. */
export function paragraphsInDiff(diffText: string): string[] {
  const out: string[] = []
  for (const block of parseChangeBlocks(diffText)) {
    for (const p of paragraphsInBlock(block)) {
      if (!out.includes(p)) out.push(p)
    }
  }
  return out
}

/** Strip the markdown syntax officers shouldn't see (bold, headings, bullets). */
export function toDisplayText(lines: string[]): string {
  return lines
    .join("\n")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^(\s*)[-*+]\s+/gm, "$1• ")
    .trim()
}

interface ProposedChangesProps {
  diffText: string
  /** Cards rendered before the "show more" button appears. */
  initialBlocks?: number
  batchSize?: number
}

export function ProposedChanges({
  diffText,
  initialBlocks = 20,
  batchSize = 20,
}: ProposedChangesProps) {
  const blocks = useMemo(() => parseChangeBlocks(diffText), [diffText])
  const [rendered, setRendered] = useState(initialBlocks)

  if (blocks.length === 0) {
    return <p className="hitl__empty">No text changes were detected in this proposal.</p>
  }

  const visible = blocks.slice(0, rendered)
  const remaining = blocks.length - rendered

  return (
    <div className="hitl__changes" data-testid="proposed-changes">
      {visible.map((b, i) => {
        const paras = paragraphsInBlock(b)
        return (
        <div className="hitl-change" key={i}>
          <div className="hitl-change__cap">
            <span>
              Edit {i + 1} of {blocks.length}
            </span>
            {paras.length > 0 && (
              <span className="hitl-change__where">¶ {paras.join(", ")}</span>
            )}
          </div>
          {b.removed.length > 0 && (
            <div className="hitl-change__block hitl-change__block--old">
              <span className="lbl">{b.added.length > 0 ? "Current text" : "Text removed"}</span>
              <p>{toDisplayText(b.removed)}</p>
            </div>
          )}
          {b.added.length > 0 && (
            <div className="hitl-change__block hitl-change__block--new">
              <span className="lbl">{b.removed.length > 0 ? "Revised text" : "Text added"}</span>
              <p>{toDisplayText(b.added)}</p>
            </div>
          )}
        </div>
        )
      })}
      {remaining > 0 && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setRendered((n) => n + batchSize)}
        >
          Show {Math.min(batchSize, remaining)} more edit{remaining > 1 ? "s" : ""} ({remaining}{" "}
          remaining)
        </button>
      )}
    </div>
  )
}
